import { Vec3 } from './Vec3.js';
import { PlaneCollider } from './Colliders.js';

/**
 * 3D Spatial Grid Partitioning for fast O(1) broadphase collision queries.
 */
export class SpatialGrid {
  constructor(cellSize = 8.0) {
    this.cellSize = cellSize;
    this.invCellSize = 1.0 / cellSize;
    this.cells = new Map();
  }

  getCellCoords(point) {
    return {
      cx: Math.floor(point.x * this.invCellSize),
      cy: Math.floor(point.y * this.invCellSize),
      cz: Math.floor(point.z * this.invCellSize)
    };
  }

  cellKey(cx, cy, cz) {
    return `${cx}_${cy}_${cz}`;
  }

  insert(collider) {
    const min = collider.bounds.min;
    const max = collider.bounds.max;

    const minCx = Math.floor(min.x * this.invCellSize);
    const maxCx = Math.floor(max.x * this.invCellSize);
    const minCy = Math.floor(min.y * this.invCellSize);
    const maxCy = Math.floor(max.y * this.invCellSize);
    const minCz = Math.floor(min.z * this.invCellSize);
    const maxCz = Math.floor(max.z * this.invCellSize);

    // Clamp cell range in case of very large bounding boxes
    const spanX = Math.min(20, maxCx - minCx);
    const spanY = Math.min(20, maxCy - minCy);
    const spanZ = Math.min(20, maxCz - minCz);

    for (let x = minCx; x <= minCx + spanX; x++) {
      for (let y = minCy; y <= minCy + spanY; y++) {
        for (let z = minCz; z <= minCz + spanZ; z++) {
          const key = this.cellKey(x, y, z);
          let cell = this.cells.get(key);
          if (!cell) {
            cell = new Set();
            this.cells.set(key, cell);
          }
          cell.add(collider);
        }
      }
    }
  }

  remove(collider) {
    for (const [key, cell] of this.cells) {
      if (cell.delete(collider)) {
        if (cell.size === 0) {
          this.cells.delete(key);
        }
      }
    }
  }

  queryAABB(min, max, outSet = new Set()) {
    const minCx = Math.floor(min.x * this.invCellSize);
    const maxCx = Math.floor(max.x * this.invCellSize);
    const minCy = Math.floor(min.y * this.invCellSize);
    const maxCy = Math.floor(max.y * this.invCellSize);
    const minCz = Math.floor(min.z * this.invCellSize);
    const maxCz = Math.floor(max.z * this.invCellSize);

    for (let x = minCx; x <= maxCx; x++) {
      for (let y = minCy; y <= maxCy; y++) {
        for (let z = minCz; z <= maxCz; z++) {
          const key = this.cellKey(x, y, z);
          const cell = this.cells.get(key);
          if (cell) {
            for (const col of cell) {
              outSet.add(col);
            }
          }
        }
      }
    }
    return outSet;
  }

  clear() {
    this.cells.clear();
  }
}

/**
 * PhysicsWorld: 60Hz Fixed-Timestep Physics Simulation Engine.
 */
export class PhysicsWorld {
  constructor(options = {}) {
    this.gravity = Vec3.from(options.gravity || { x: 0, y: -18.0, z: 0 }); // g = -18 m/s^2
    this.fixedDeltaTime = options.fixedDeltaTime || 1.0 / 60.0; // 60Hz
    this.maxSubSteps = options.maxSubSteps || 8;
    this.accumulator = 0.0;
    this.time = 0.0;

    this.colliders = [];
    this.infiniteColliders = [];
    this.characters = [];
    this.spatialGrid = new SpatialGrid(options.gridCellSize || 8.0);

    // Default infinite ground plane if requested or by default
    if (options.addGroundPlane !== false) {
      this.groundPlane = new PlaneCollider({
        name: 'ground_plane',
        point: { x: 0, y: 0, z: 0 },
        normal: { x: 0, y: 1, z: 0 }
      });
      this.addCollider(this.groundPlane);
    }
  }

  addCollider(collider) {
    if (!collider) return;
    this.colliders.push(collider);

    // Infinite planes are stored separately to avoid flood filling grid
    if (collider instanceof PlaneCollider && !collider.size) {
      this.infiniteColliders.push(collider);
    } else {
      this.spatialGrid.insert(collider);
    }
    return collider;
  }

  removeCollider(collider) {
    const idx = this.colliders.indexOf(collider);
    if (idx !== -1) {
      this.colliders.splice(idx, 1);
    }
    const infIdx = this.infiniteColliders.indexOf(collider);
    if (infIdx !== -1) {
      this.infiniteColliders.splice(infIdx, 1);
    } else {
      this.spatialGrid.remove(collider);
    }
  }

  addCharacter(character) {
    if (!character || this.characters.includes(character)) return;
    this.characters.push(character);
    character.setPhysicsWorld(this);
    return character;
  }

  removeCharacter(character) {
    const idx = this.characters.indexOf(character);
    if (idx !== -1) {
      this.characters.splice(idx, 1);
      character.setPhysicsWorld(null);
    }
  }

  /**
   * Query all colliders that might intersect an AABB bounding volume.
   */
  queryAABB(min, max) {
    const results = new Set();
    // Include infinite colliders
    for (const col of this.infiniteColliders) {
      results.add(col);
    }
    this.spatialGrid.queryAABB(min, max, results);
    return Array.from(results);
  }

  /**
   * Query colliders intersecting a sphere.
   */
  querySphere(center, radius) {
    const min = new Vec3(center.x - radius, center.y - radius, center.z - radius);
    const max = new Vec3(center.x + radius, center.y + radius, center.z + radius);
    return this.queryAABB(min, max);
  }

  /**
   * Query colliders intersecting a capsule segment.
   */
  queryCapsule(baseCenter, topCenter, radius) {
    const min = new Vec3(
      Math.min(baseCenter.x, topCenter.x) - radius,
      Math.min(baseCenter.y, topCenter.y) - radius,
      Math.min(baseCenter.z, topCenter.z) - radius
    );
    const max = new Vec3(
      Math.max(baseCenter.x, topCenter.x) + radius,
      Math.max(baseCenter.y, topCenter.y) + radius,
      Math.max(baseCenter.z, topCenter.z) + radius
    );
    return this.queryAABB(min, max);
  }

  /**
   * Cast a ray against all colliders in the world and return the closest hit.
   */
  raycast(rayOrigin, rayDir, maxDistance = Infinity, filter = null) {
    const origin = Vec3.from(rayOrigin);
    const dir = Vec3.from(rayDir).normalize();

    let closestHit = null;
    let closestDist = maxDistance;

    // Test all candidate colliders
    for (const col of this.colliders) {
      if (filter && !filter(col)) continue;
      const hit = col.raycast(origin, dir, closestDist);
      if (hit && hit.distance < closestDist) {
        closestDist = hit.distance;
        closestHit = hit;
      }
    }

    return closestHit;
  }

  /**
   * Step the physics simulation using a 60Hz fixed accumulator.
   */
  step(deltaTime) {
    // Limit large dt jumps (e.g. tab unfocus) to prevent spiral of death
    const dtClamped = Math.min(deltaTime, 0.1);
    this.accumulator += dtClamped;

    let subSteps = 0;
    while (this.accumulator >= this.fixedDeltaTime && subSteps < this.maxSubSteps) {
      this.fixedStep(this.fixedDeltaTime);
      this.accumulator -= this.fixedDeltaTime;
      this.time += this.fixedDeltaTime;
      subSteps++;
    }
  }

  /**
   * Internal single 60Hz fixed timestep update.
   */
  fixedStep(fixedDt) {
    for (const char of this.characters) {
      char.fixedUpdate(fixedDt);
    }
  }

  clear() {
    this.colliders = [];
    this.infiniteColliders = [];
    this.characters = [];
    this.spatialGrid.clear();
    this.accumulator = 0;
    this.time = 0;
  }
}
